import * as React from 'react';

import { Button } from '@/ui/button';
import { cn } from '@/utils';

import BookIcon from '../../assets/new_course.svg';

import { IMODrawer } from './imo-drawer';

/**
 * Card title for IMO enrollment notification
 */
const IMO_CARD_TITLE = 'New Math Olympiad Course Added';

/**
 * Card subtitle for IMO enrollment notification
 */
const IMO_CARD_SUBTITLE = 'Enroll now in International Math Olympiad';

/**
 * Alt text for IMO card icon
 */
const IMO_ICON_ALT = 'International Math Olympiad course icon';

/**
 * Props for IMOCard component
 * Currently accepts no props, but interface is defined for future extensibility
 */

type IMOCardProps = Record<string, unknown>;

/**
 * IMO (International Math Olympiad) enrollment notification card
 *
 * Displays a notification card for IMO course enrollment.
 * Clicking "View" opens a drawer with the enrollment form.
 *
 * @example
 * ```tsx
 * <IMOCard ref={cardRef} />
 * ```
 */
export const IMOCard = React.forwardRef<HTMLDivElement, IMOCardProps>(
  (_, ref) => {
    const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);

    const handleClick = React.useCallback(() => {
      setIsDrawerOpen(true);
    }, []);

    const handleCloseDrawer = React.useCallback(() => {
      setIsDrawerOpen(false);
    }, []);

    return (
      <>
        <div
          ref={ref}
          role="article"
          aria-labelledby="imo-card-title"
          aria-describedby="imo-card-description"
          className={cn(
            'bg-white/60 rounded-[14px] p-4',
            'flex items-center gap-4',
            'transition-all duration-200',
            'hover:shadow-lg'
          )}
        >
          {/* Icon Container */}
          <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center">
            <img src={BookIcon} alt={IMO_ICON_ALT} className="w-12 h-12" />
          </div>

          {/* Content */}
          <div className="flex-1">
            <h3
              id="imo-card-title"
              className="text-sm font-semibold text-gray-900"
            >
              {IMO_CARD_TITLE}
            </h3>
            <p
              id="imo-card-description"
              className="text-xs text-gray-600 mt-0.5"
            >
              {IMO_CARD_SUBTITLE}
            </p>
          </div>

          {/* Action Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleClick}
            className="flex-shrink-0"
            aria-label="View International Math Olympiad course details"
            aria-describedby="imo-card-description"
          >
            View
          </Button>
        </div>

        {/* Drawer */}
        <IMODrawer isOpen={isDrawerOpen} onClose={handleCloseDrawer} />
      </>
    );
  }
);

IMOCard.displayName = 'IMOCard';
